import {
  BookOpen, Dumbbell, Brain, BriefcaseBusiness, HeartHandshake,
  CircleDollarSign, Home, Crown,
} from "lucide-react";

// ---------------- Life areas (8 operating blueprints) ----------------
export const areas = [
  { name: "Spiritual Governance", short: "Faith", icon: BookOpen, color: "#1f7355", keys: ["spiritual", "faith", "identity"], question: "Are my choices rooted in faith, character and obedience?" },
  { name: "Health & Vitality", short: "Body", icon: Dumbbell, color: "#34705f", keys: ["health", "body", "vitality", "wellness"], question: "Is my body supported by sustainable care and energy?" },
  { name: "Mind & Learning", short: "Mind", icon: Brain, color: "#416a87", keys: ["mind", "learning", "education", "growth"], question: "Am I sharpening my judgment, knowledge and emotional steadiness?" },
  { name: "Career & Calling", short: "Calling", icon: BriefcaseBusiness, color: "#7e4b31", keys: ["career", "calling", "work", "dba", "business"], question: "Is my work moving the assignment entrusted to me forward?" },
  { name: "Relationships", short: "People", icon: HeartHandshake, color: "#a0445f", keys: ["relationship", "family", "community", "people"], question: "Am I present, generous and well connected to the right people?" },
  { name: "Wealth & Stewardship", short: "Wealth", icon: CircleDollarSign, color: "#8a692c", keys: ["wealth", "finance", "money", "stewardship"], question: "Are my resources aligned with protection, freedom and purpose?" },
  { name: "Home & Environment", short: "Home", icon: Home, color: "#6f7052", keys: ["home", "environment", "space"], question: "Does my environment reduce friction and support the life I want?" },
  { name: "Presence & Restoration", short: "Presence", icon: Crown, color: "#6b4c88", keys: ["presence", "beauty", "rest", "restoration", "joy"], question: "Am I protecting restoration and showing up with intention?" },
];

export const horizons = ["Day", "Week", "Month", "Quarter", "Year"];
export const kinds = ["Goal", "Project", "Routine", "Metric", "Milestone"];

// Blueprint types tailored to each life area so they make sense in context.
export const areaKinds = {
  "Spiritual Governance": ["Practice", "Study", "Discipline"],
  "Health & Vitality": ["Habit", "Routine", "Metric"],
  "Mind & Learning": ["Study", "Practice", "Metric"],
  "Career & Calling": ["Project", "Milestone", "Metric"],
  Relationships: ["Rhythm", "Commitment", "Milestone"],
  "Wealth & Stewardship": ["Habit", "Goal", "Metric"],
  "Home & Environment": ["System", "Project", "Routine"],
  "Presence & Restoration": ["Ritual", "Boundary", "Practice"],
};
export function kindsForArea(name) {
  return areaKinds[name] || kinds;
}
// Map any blueprint kind to a status-pill tone.
export function kindTone(kind) {
  const k = (kind || "").toLowerCase();
  if (["metric"].includes(k)) return "review";
  if (["project", "milestone", "goal", "system"].includes(k)) return "active";
  return "protected"; // habit, routine, practice, study, discipline, rhythm, commitment, ritual, boundary
}
export const priorities = ["Critical", "High", "Medium", "Low"];
export const cadences = ["Daily", "Weekdays", "Weekly", "Monthly", "This quarter", "As needed"];

// ---------------- Planning meetings ----------------
export const planningMeetings = {
  Year: {
    title: "Yearly Vision Meeting",
    purpose: "Set the direction of the year before assigning goals, seasons and recurring responsibilities.",
    prompts: [
      { id: "word", title: "Word for the year", help: "What word should govern how you live and lead this year?", short: true },
      { id: "vision", title: "Whole-life vision", help: "Describe the woman you are becoming and what a faithful, flourishing year looks like." },
      { id: "outcomes", title: "Yearly outcomes", help: "Which meaningful goals and healthy rhythms deserve focused pursuit?" },
      { id: "events", title: "Life events and seasons", help: "Capture transitions, deadlines, travel, health needs and demanding seasons the plan must respect." },
      { id: "schedule", title: "Goal scheduling", help: "Assign goals to quarters so you can pursue them without spreading yourself too thin." },
      { id: "recurring", title: "Recurring stewardship", help: "List yearly and quarterly responsibilities that should be remembered automatically." },
    ],
  },
  Quarter: {
    title: "Quarterly Alignment Meeting",
    purpose: "Revisit mindset, life vision, priorities, routines and the next three months for your current season.",
    prompts: [
      { id: "belief", title: "Mindset reset", help: "Name the limiting belief, what it is rooted in and the truthful belief that will replace it." },
      { id: "lifeMap", title: "Life vision map", help: "What matters most this quarter across faith, body, mind, calling, relationships, wealth, home and restoration?" },
      { id: "priorities", title: "Priority rhythms", help: "Which daily, weekly, monthly and occasional commitments are genuinely non-negotiable?" },
      { id: "routines", title: "Routines and schedule", help: "Which routines need a name, frequency, time and clear set of activities?" },
      { id: "months", title: "Three-month map", help: "For each month, name the focus goal and the dates or life events that affect capacity." },
      { id: "idealWeek", title: "Ideal week", help: "Sketch the repeatable weekly outline that supports work, worship, health, people, home and rest." },
    ],
  },
  Month: {
    title: "Monthly Planning Meeting",
    purpose: "Translate quarterly direction into focused goals, relationships, rest, dates and a realistic task pool.",
    prompts: [
      { id: "affirmation", title: "Monthly affirmation", help: "What truthful sentence do you need to remember this month?", short: true },
      { id: "goals", title: "Focus goals", help: "Which one to three outcomes receive focused attention this month?" },
      { id: "relationship", title: "Relationship to nurture", help: "Who needs intentional care, and how will you show up for them?" },
      { id: "rest", title: "Rest and self-care", help: "What will restore you before exhaustion makes the decision?" },
      { id: "dates", title: "Important dates", help: "Capture appointments, deadlines, celebrations and events that shape the month." },
      { id: "tasks", title: "Monthly task pool", help: "Capture priority, personal, professional and other tasks without forcing everything into one week." },
    ],
  },
  Week: {
    title: "Weekly Preparation Meeting",
    purpose: "Choose the week's outcomes, protect rest and turn the month's plan into scheduled commitments.",
    prompts: [
      { id: "top1", title: "Week's Top 3 — first", help: "The single most important outcome this week.", short: true },
      { id: "top2", title: "Week's Top 3 — second", help: "The second most important outcome this week.", short: true },
      { id: "top3", title: "Week's Top 3 — third", help: "The third most important outcome this week.", short: true },
      { id: "tasks", title: "Supporting tasks", help: "Which other tasks truly belong in this week—not merely on your mind?" },
      { id: "rest", title: "How I am resting", help: "Where is restoration deliberately protected?" },
      { id: "relationships", title: "People and presence", help: "Who needs a conversation, touchpoint or act of care?" },
    ],
  },
  Day: {
    title: "Daily Planning Meeting",
    purpose: "Take ownership of today's time and energy while leaving room for your real capacity.",
    prompts: [
      { id: "gratitude", title: "I am grateful for", help: "Begin from gratitude rather than urgency.", short: true },
      { id: "feeling", title: "Today I want to feel", help: "Name the quality you want to carry through the day.", short: true },
      { id: "focus3", title: "Today's Focus 3", help: "Which three tasks deserve your best time and energy?" },
      { id: "other", title: "Other tasks", help: "What supporting tasks can follow after the Focus 3 is protected?" },
      { id: "kindness", title: "Intentional kindness", help: "Plan one kind thing for yourself and one for someone else." },
      { id: "notes", title: "Notes and reminders", help: "What must be remembered without carrying it in your head?" },
    ],
  },
};

// ---------------- Review meetings ----------------
export const reviewMeetings = {
  Day: {
    title: "Daily Closing Review",
    purpose: "Close the loop on energy, alignment and tomorrow without putting your worth on trial.",
    prompts: [
      { id: "grace", title: "Grace and gratitude", help: "What moment, provision or person are you grateful for today?" },
      { id: "progress", title: "Meaningful progress", help: "What important action did you complete or move forward?" },
      { id: "energy", title: "Energy truth", help: "What gave you energy, and what drained it?" },
      { id: "alignment", title: "Alignment check", help: "Where did your choices align—or not align—with faith, values and priorities?" },
      { id: "release", title: "Release the day", help: "What can be forgiven, deferred or left here?" },
      { id: "tomorrow", title: "Tomorrow's first move", help: "What one action will make tomorrow easier to lead?" },
    ],
  },
  Week: {
    title: "Weekly Review",
    purpose: "Evaluate outcomes, balance, relationships and rest across the whole week—not one imperfect day.",
    prompts: [
      { id: "wins", title: "Wins and evidence", help: "What worked, and what measurable progress deserves recognition?" },
      { id: "top3", title: "Outcome review", help: "Which Top 3 outcomes were completed, advanced or intentionally changed?" },
      { id: "balance", title: "Life-area balance", help: "Which area received enough care, and which needs a gentle lift?" },
      { id: "rest", title: "Rest and capacity", help: "Did the schedule respect your energy or borrow from next week?" },
      { id: "people", title: "Relationships", help: "How did you show up for the people God entrusted to you?" },
      { id: "next", title: "Next week's focus", help: "What three outcomes and one boundary should shape the coming week?" },
    ],
  },
  Month: {
    title: "Monthly Review",
    purpose: "Review goal movement, routines, relationships, rest and stewardship before choosing the next month's focus.",
    prompts: [
      { id: "goals", title: "Goal movement", help: "Which focus goals advanced, stalled or changed—and what does the evidence say?" },
      { id: "rhythms", title: "Routines and habits", help: "Which routines supported your life, and which created friction?" },
      { id: "relationship", title: "Relationship nurtured", help: "Who received intentional care, and who needs attention next month?" },
      { id: "rest", title: "Rest and well-being", help: "What restored you, depleted you or now requires adjustment?" },
      { id: "stewardship", title: "Life stewardship", help: "What needs attention in home, finances, administration or environment?" },
      { id: "switch", title: "Start, stop, continue", help: "What will you start, stop and continue next month?" },
      { id: "next", title: "Next month's focus", help: "Name the goals, relationship, rest plan and dates that will govern the next month." },
    ],
  },
  Quarter: {
    title: "Quarterly Review",
    purpose: "Reassess identity, vision, priorities, routines and capacity before committing to another three months.",
    prompts: [
      { id: "season", title: "Season assessment", help: "What changed in responsibilities, capacity or calling?" },
      { id: "mindset", title: "Mindset and truth", help: "Which limiting belief surfaced, what fed it and what truth replaces it?" },
      { id: "vision", title: "Whole-life vision", help: "How did each life area move toward—or away from—the life you intend to build?" },
      { id: "goals", title: "Goal portfolio", help: "Which goals were completed, advanced, paused or released—and why?" },
      { id: "priorities", title: "Priorities and routines", help: "Which rhythms should be protected, redesigned or removed?" },
      { id: "capacity", title: "Capacity and peace", help: "Where were you peacefully productive, and where did ambition become expensive?" },
      { id: "next", title: "Next-quarter direction", help: "What theme, outcomes, routines and boundaries will govern the next three months?" },
    ],
  },
  Year: {
    title: "Yearly Life Review",
    purpose: "Honor the year honestly, discern what it formed in you and establish the foundation for the year ahead.",
    prompts: [
      { id: "story", title: "The story of the year", help: "What events, decisions, relationships and seasons most shaped this year?" },
      { id: "becoming", title: "Who you became", help: "How did you grow in faith, character, health, leadership and stewardship?" },
      { id: "goals", title: "Yearly outcomes", help: "Which goals were achieved, changed, delayed or released—and what did each teach you?" },
      { id: "areas", title: "Whole-life review", help: "What evidence shows strength or need across every life area?" },
      { id: "rhythms", title: "Rhythms that shaped the year", help: "Which routines, boundaries and recurring commitments protected what mattered?" },
      { id: "release", title: "Release and carry forward", help: "What are you leaving behind, and what wisdom must come with you?" },
      { id: "next", title: "Direction for the new year", help: "What word, vision, outcomes and seasonal schedule will guide the year ahead?" },
    ],
  },
};

// ---------------- Daily verses (KJV) ----------------
export const dailyVerses = [
  { reference: "Proverbs 16:3", text: "Commit thy works unto the Lord, and thy thoughts shall be established." },
  { reference: "Isaiah 41:10", text: "Fear thou not; for I am with thee: be not dismayed; for I am thy God." },
  { reference: "Philippians 4:13", text: "I can do all things through Christ which strengtheneth me." },
  { reference: "Psalm 90:17", text: "Establish thou the work of our hands upon us; yea, the work of our hands establish thou it." },
  { reference: "Romans 12:2", text: "Be ye transformed by the renewing of your mind, that ye may prove what is that good, and acceptable, and perfect, will of God." },
  { reference: "Galatians 6:9", text: "Let us not be weary in well doing: for in due season we shall reap, if we faint not." },
  { reference: "Ephesians 2:10", text: "For we are his workmanship, created in Christ Jesus unto good works." },
  { reference: "Psalm 46:5", text: "God is in the midst of her; she shall not be moved: God shall help her, and that right early." },
  { reference: "James 1:5", text: "If any of you lack wisdom, let him ask of God, that giveth to all men liberally." },
  { reference: "Matthew 6:34", text: "Take therefore no thought for the morrow: for the morrow shall take thought for the things of itself." },
  { reference: "Colossians 3:23", text: "Whatsoever ye do, do it heartily, as to the Lord, and not unto men." },
  { reference: "Psalm 37:5", text: "Commit thy way unto the Lord; trust also in him; and he shall bring it to pass." },
];

export const areaEncouragement = {
  "Spiritual Governance": { title: "Return to the source before the strategy.", message: "A demanding season is not a reason to loosen your spiritual foundation. Let prayer and Scripture govern the pace, not merely decorate it.", action: "Protect one quiet appointment with God" },
  "Health & Vitality": { title: "Your body is infrastructure, not an interruption.", message: "You do not need a heroic health day. Choose the next supportive decision that gives tomorrow more strength.", action: "Complete one body-supporting action" },
  "Mind & Learning": { title: "Clarity grows when your mind has room to process.", message: "Protect one focused learning or reflection block. You are building judgment, not collecting more information.", action: "Create one focused thinking block" },
  "Career & Calling": { title: "Move the assignment, not just the inbox.", message: "Your calling advances through a clear finish line. Give your best attention to the outcome with the highest strategic value.", action: "Advance one meaningful milestone" },
  Relationships: { title: "Presence is also productive.", message: "A thoughtful touchpoint can strengthen the relationships that carry this life with you. Do not let urgency make people invisible.", action: "Reach out to one important person" },
  "Wealth & Stewardship": { title: "Stewardship creates options before you need them.", message: "One honest financial action today can reduce future pressure. Focus on clarity, protection and purposeful use of resources.", action: "Complete one money stewardship action" },
  "Home & Environment": { title: "Let your environment serve the woman you are becoming.", message: "A small reset can remove tomorrow's friction. Choose the space or system that would give you the most peace and ease.", action: "Reset one high-friction space" },
  "Presence & Restoration": { title: "Restoration protects your excellence.", message: "You do not have to earn rest by reaching exhaustion. Make room for beauty, joy or stillness before your capacity makes the decision for you.", action: "Protect one restorative moment" },
};

// ---------------- Helpers ----------------
export function iso(date = new Date()) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}
export function uid(prefix = "id") {
  return prefix + "-" + Date.now() + "-" + Math.random().toString(36).slice(2, 7);
}
export function cap(value) {
  return Math.max(0, Math.min(100, Math.round(Number.isFinite(value) ? value : 0)));
}
export function stripHtml(html) {
  if (!html) return "";
  return String(html).replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
}
export function pointsFor(priority) {
  const value = (priority || "").toLowerCase();
  if (value.includes("critical") || value.includes("must")) return 25;
  if (value.includes("high")) return 15;
  if (value.includes("low")) return 5;
  return 10;
}
export function label(date, options) {
  return new Date(date + "T12:00:00").toLocaleDateString("en-US", options || { weekday: "long", month: "long", day: "numeric" });
}
export function areaMatch(value, area) {
  const text = (value || "").toLowerCase();
  return text === area.name.toLowerCase() || area.keys.some((key) => text.includes(key));
}
function monday(date) {
  const value = new Date(date + "T12:00:00");
  const day = value.getDay();
  value.setDate(value.getDate() - (day === 0 ? 6 : day - 1));
  return value;
}
function quarter(date) {
  return Math.floor(new Date(date + "T12:00:00").getMonth() / 3) + 1;
}
export function periodKey(horizon, date) {
  const current = new Date(date + "T12:00:00");
  if (horizon === "Day") return date;
  if (horizon === "Week") return iso(monday(date));
  if (horizon === "Month") return String(current.getFullYear()) + "-" + String(current.getMonth() + 1).padStart(2, "0");
  if (horizon === "Quarter") return String(current.getFullYear()) + "-Q" + String(quarter(date));
  return String(current.getFullYear());
}
export function lifePlanKey(horizon, date) {
  return horizon + ":" + periodKey(horizon, date);
}
export function verseFor(date) {
  const [year, month, day] = date.split("-").map(Number);
  const index = Math.abs(Math.floor(Date.UTC(year, month - 1, day) / 86400000)) % dailyVerses.length;
  return dailyVerses[index];
}
export function timeLabel(task) {
  const format = (value) => {
    if (!value) return "Flexible";
    const split = value.split(":").map(Number);
    return String(split[0] % 12 || 12) + ":" + String(split[1] || 0).padStart(2, "0") + " " + (split[0] >= 12 ? "PM" : "AM");
  };
  return task.endTime ? format(task.time) + " - " + format(task.endTime) : format(task.time);
}

// ---------------- Recurring actions ----------------
// A task either happens once (bound to `date`) or repeats. Repeating tasks show
// up automatically on every matching day, so they never need re-adding.
export const recurrenceOpts = [
  { value: "once", label: "Just this day" },
  { value: "daily", label: "Every day" },
  { value: "weekdays", label: "Weekdays (Mon–Fri)" },
  { value: "weekly", label: "Weekly (same weekday)" },
];
export const recurrenceLabel = { once: "Once", daily: "Daily", weekdays: "Weekdays", weekly: "Weekly" };

function weekdayIndex(date) {
  return new Date(date + "T12:00:00").getDay(); // 0 Sun .. 6 Sat
}

// Does a task occur on the given ISO date?
export function taskOccursOn(task, date) {
  const rec = task.recurrence || "once";
  if (rec === "once") return !task.date || task.date === date;
  if (task.date && date < task.date) return false; // don't appear before its start day
  if (task.until && date > task.until) return false;
  if (rec === "daily") return true;
  if (rec === "weekdays") { const d = weekdayIndex(date); return d >= 1 && d <= 5; }
  if (rec === "weekly") return !task.date || weekdayIndex(date) === weekdayIndex(task.date);
  return false;
}

// Is the task complete for the given date? Recurring tasks track per-day completion.
export function taskDoneOn(task, date) {
  if ((task.recurrence || "once") === "once") return !!task.done;
  return Array.isArray(task.doneDates) && task.doneDates.includes(date);
}

export function isRecurring(task) {
  return (task?.recurrence || "once") !== "once";
}

// ---------------- Habit tracker ----------------
export function habitDoneOn(log, id, date) {
  return !!(log && log[id] && log[id][date]);
}
// Consecutive completed days ending on (and including) `date`.
export function habitStreak(log, id, date) {
  let n = 0;
  const d = new Date(date + "T12:00:00");
  while (log && log[id] && log[id][iso(d)]) { n++; d.setDate(d.getDate() - 1); }
  return n;
}
// Total completions recorded for a habit.
export function habitTotal(log, id) {
  return log && log[id] ? Object.keys(log[id]).length : 0;
}
// The last N ISO dates ending on `date` (oldest first).
export function lastNDates(date, n = 7) {
  const out = [];
  const base = new Date(date + "T12:00:00");
  for (let i = n - 1; i >= 0; i--) { const c = new Date(base); c.setDate(base.getDate() - i); out.push(iso(c)); }
  return out;
}
// Longest run of consecutive completed days ever recorded for a habit.
export function habitBestStreak(log, id) {
  const days = log && log[id] ? Object.keys(log[id]).filter((d) => log[id][d]).sort() : [];
  if (!days.length) return 0;
  let best = 1, cur = 1;
  for (let i = 1; i < days.length; i++) {
    const prev = new Date(days[i - 1] + "T12:00:00");
    const curr = new Date(days[i] + "T12:00:00");
    const diff = Math.round((curr - prev) / 86400000);
    if (diff === 1) { cur += 1; best = Math.max(best, cur); } else { cur = 1; }
  }
  return best;
}
// Calendar grid (weeks of 7 cells, Sun-first) for a given year/month (month = 0-11).
// Cells before the 1st and after the last day are null.
export function monthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(iso(new Date(year, month, d)));
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}
// Completed / total-days / rate for a habit within a given month.
export function habitMonthStats(log, id, year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const map = (log && log[id]) || {};
  let done = 0;
  for (let d = 1; d <= daysInMonth; d++) { if (map[iso(new Date(year, month, d))]) done += 1; }
  return { done, total: daysInMonth, rate: daysInMonth ? Math.round((done / daysInMonth) * 100) : 0 };
}
export function monthLabel(year, month) {
  return new Date(year, month, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

// ---------------- Scoring / metrics ----------------
export function computeLifeMetrics(state, date, overrides = {}) {
  const tasks = state.tasks || [];
  const goals = state.goals || [];
  const components = state.components || [];
  const dayTasks = tasks.filter((task) => taskOccursOn(task, date));
  const planned = dayTasks.reduce((sum, task) => sum + (task.points || pointsFor(task.priority)), 0);
  const earned = Math.min(100, dayTasks.filter((t) => taskDoneOn(t, date)).reduce((sum, t) => sum + (t.points || pointsFor(t.priority)), 0));

  const areaScores = areas.map((area) => {
    if (Number.isFinite(overrides[area.name])) return { area, score: cap(overrides[area.name]) };
    const at = tasks.filter((t) => areaMatch(t.engine, area));
    const ag = goals.filter((g) => areaMatch(g.engine, area));
    const ai = components.filter((c) => areaMatch(c.area, area));
    const signals = [];
    if (at.length) {
      const total = at.reduce((s, t) => s + (t.points || pointsFor(t.priority)), 0);
      const complete = at.filter((t) => taskDoneOn(t, date)).reduce((s, t) => s + (t.points || pointsFor(t.priority)), 0);
      signals.push(total ? (complete / total) * 100 : 0);
    }
    if (ag.length) signals.push(ag.reduce((s, g) => s + g.progress, 0) / ag.length);
    if (ai.length) signals.push(ai.reduce((s, c) => s + c.progress, 0) / ai.length);
    if (!signals.length && Number.isFinite(state.engineScores?.[area.name])) signals.push(state.engineScores[area.name]);
    return { area, score: signals.length ? cap(signals.reduce((s, v) => s + v, 0) / signals.length) : 0 };
  });

  const scored = areaScores.filter((i) => i.score > 0);
  const lifeScore = scored.length ? cap(scored.reduce((s, i) => s + i.score, 0) / scored.length) : 0;
  const lowest = [...areaScores].filter((i) => i.score > 0).sort((a, b) => a.score - b.score)[0] || areaScores[0];
  const next = [...dayTasks].filter((t) => !taskDoneOn(t, date)).sort((a, b) => (b.points || 0) - (a.points || 0))[0];

  const brief = earned >= 80
    ? { signal: "Strong execution", title: "The day is already won. Finish with care.", detail: String(earned) + " points are secured. Complete the final commitments without borrowing from tomorrow." }
    : (state.energy < 45
      ? { signal: "Capacity risk", title: "Protect the essential; reduce the decorative.", detail: next ? "Complete \u201c" + next.title + "\u201d first, then reassess the remaining plan." : "Keep faith, health and the most important outcome protected." }
      : { signal: next ? "Priority identified" : "Plan complete", title: next ? "Your next best move is \u201c" + next.title + ".\u201d" : "Close the loop with reflection and rest.", detail: next ? "It carries " + String(next.points || pointsFor(next.priority)) + " points and has the strongest immediate effect on today's score." : "Record what worked so the system gets smarter." });

  return { dayTasks, planned, earned, areaScores, scored, lifeScore, lowest, next, brief };
}
